import React from 'react';
import { CollapseFAQ, H3, LayoutSingleColumn, Page } from '../../components';
import SectionFooter from '../PageBuilder/SectionBuilder/SectionFooter';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import { useIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';

import { defaultFaq } from './faq';
import CatImage from './images/SMALL Cat Sitting on sofa back in Gers FRANCE.jpg';
import DogImage from './images/HouseSit Match 2023-117.jpg'
import css from './BenefitsHomeownersPage.module.css';

const BenefitsHomeownersPage = props => {

  const config = useConfiguration();
  const intl = useIntl();

  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage({ id: 'BenefitsHomeownersPage.schemaTitle' }, { marketplaceName });
  const schemaDescription = intl.formatMessage({ id: 'BenefitsHomeownersPage.schemaDescription' });

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<SectionFooter />}
        mainColumnClassName={css.mainColumnClassName}
      >
        <div className={css.root}>
          <H3>
            Benefits of House Sitting for Homeowners and Pet Owners
          </H3>

          <p className={css.info}>
            Finding trusted care for your home and pets doesn’t have to be a challenge. HouseSit Match bridges the gap between homeowners and pet owners seeking reliable sitters and those willing to care for homes and pets in exchange for accommodation.
          </p>

          <p className={css.info}>
            With verified sitters, tailored membership plans, and global coverage, we make it easier than ever to travel with peace of mind.
          </p>

          <div className={css.imageContainer}>
            <img className={css.image} src={CatImage} alt='pets remaining relaxed at home is one of the benefits of house sitting for homeowners' />
            <p className={css.info}>
              Pets staying at home relaxed in their own environment, is one of the main benefits of house sitting for homeowners and pet owners
            </p>
          </div>

          <p className={css.subtitle}>
            Advantages for Homeowners
          </p>
          <p className={css.info}>
            House sitting offers a smart, cost-effective way to ensure your property remains secure and well-maintained during your absence. Beyond simply having someone occupy your home, this arrangement provides additional benefits that help protect your investment.
          </p>
          <ul className={css.list}>
            <li>
              <strong>Property Security: </strong>An occupied home is less likely to attract intruders, giving you peace of mind while you’re away.
            </li>
            <li>
              <strong>Cost Savings: </strong>Avoid paying for professional caretakers or temporary property managers.
            </li>
            <li>
              <strong>Peace of Mind: </strong>Verified sitters with real reviews ensure you’re leaving your home in trustworthy hands.
            </li>
            <li>
              <strong>Ongoing Maintenance: </strong>Sitters can handle routine tasks such as watering plants, keeping gardens tidy, and addressing minor household needs.
            </li>
            <li>
              <strong>Flexible Scheduling: </strong>House sitting allows you to arrange care for both short trips and long-term absences.
            </li>
          </ul>
          <p className={css.info}>
            Homeowners gain security and convenience, knowing their property is cared for without any additional expenses.
          </p>


          <p className={css.subtitle}>
            Advantages for Pet Owners
          </p>
          <p className={css.info}>
            For pet owners, house sitting offers a unique opportunity to ensure your beloved animals receive personalised care while staying in their familiar environment. This not only reduces pet stress but also provides significant cost savings.
          </p>
          <ul className={css.list}>
            <li>
              <strong>Comfort in Familiarity: </strong>Pets can remain in their usual surroundings, avoiding the stress of a boarding facility.
            </li>
            <li>
              <strong>Routine Care: </strong>Sitters can follow your exact feeding, exercise, and medication schedules, ensuring continuity.
            </li>
            <li>
              <strong>Cost Efficiency: </strong>Eliminate the need for expensive kennelling or pet boarding fees.
            </li>
            <li>
              <strong>Trusted Companionship: </strong>All sitters are pet lovers, offering dedicated and attentive care tailored to your pet’s needs.
            </li>
            <li>
              <strong>Tailored Attention: </strong>Sitters can adapt to your pet’s unique needs, such as dietary restrictions or social preferences.
            </li>
          </ul>
          <p className={css.info}>
            By using pet sitting services, you’re giving your pets the gift of consistent and loving care while keeping costs low.
          </p>
          <div className={css.imageContainer}>
            <img className={css.image} src={DogImage} alt="housesitter comes to the house to meet the pet owners and the pet" />
            <p className={css.info}>
              The fact that the house sitter comes to your home to care for the pets and property is a great benefit of house sitting for home and pet owners
            </p>
          </div>


          <p className={css.subtitle}>
            Why Choose HouseSit Match for Your Home and Pets?
          </p>
          <p className={css.info}>
            Choosing a trusted platform like HouseSit Match ensures you get the right sitters for your specific needs. Our service is designed to provide a hassle-free and transparent process from start to finish.
          </p>
          <ul className={css.list}>
            <li>
              <strong>Verified Sitters: </strong>Every sitter undergoes rigorous ID checks to guarantee their authenticity and reliability.
            </li>
            <li>
              <strong>Global Network: </strong>Whether you need sitters locally or internationally, our platform connects you to a worldwide community.
            </li>
            <li>
              <strong>Genuine Reviews: </strong>All feedback and reviews come from real sits arranged through our platform, giving you confidence in your choice.
            </li>
            <li>
              <strong>Comprehensive Support: </strong>From live chat to detailed resources, our team ensures you’re supported every step of the way.
            </li>
            <li>
              <strong>Member Resources: </strong>Exclusive access to pet care guides, tips, and tools ensures a smoother house sitting experience.
            </li>
            <li>
              <strong>Combined Homeowner and House Sitter Membership :</strong> Increasingly our members are opting for a combined membership (link to new page) to give themselves the opportunity to find house sitters or house sit themselves.
            </li>
          </ul>
          <p className={css.info}>
            HouseSit Match takes the guesswork out of finding trusted sitters, letting you focus on your travel plans with complete confidence.
          </p>

          <p className={css.subtitle}>
            How HouseSit Match Supports Pet Owners
          </p>
          <p className={css.info}>
            We go beyond simply connecting you with sitters. HouseSit Match provides a range of tools and services to simplify your experience and make house and pet sitting arrangements seamless.
          </p>
          <ul className={css.list}>
            <li>
              <strong>Guides and Templates: </strong>From pet care checklists to house sit templates, we help you clearly communicate your expectations.
            </li>
            <li>
              <strong>Dedicated Support: </strong>Get in touch with our team via live chat, WhatsApp, or email whenever you need assistance.
            </li>
            <li>
              <strong>Safety First: </strong>Our ID verification process and secure platform give you confidence in every connection.
            </li>
            <li>
              <strong>Free Video Calls - </strong>Meet your house sitters online, get to know them and keep in contact if you need to.
            </li>
            <li>
              <strong>Exclusive Insights: </strong>Gain access to our member-only resources, including pet care guides and practical advice on property management.
            </li>
            <li>
              <strong>Tailored Recommendations: </strong>Receive tips and advice specifically suited to your needs as a pet owner.
            </li>
            <li>
              <strong>24/7 Access to Vet Professionals: </strong>Call a professional vet nurse with minimum 5 years experience in case you need a consultation for your pet. They will help you decide how to support your pet in case of unexpected poor health.
            </li>

          </ul>
          <p className={css.info}>
            HouseSit Match isn’t just a platform; it’s a partner in helping you achieve stress-free house and pet sitting.
          </p>

          <p className={css.subtitle}>
            Flexible Membership Options
          </p>
          <p className={css.info}>
            At HouseSit Match, we believe in giving homeowners and pet owners the flexibility to choose the membership that works best for them.
          </p>

          <ul className={css.list}>
            <li>
              <div className='mt20'>
                <strong>VALUE MATCH MEMBERSHIP £89/year</strong>
              </div>
              <ul>
                <li>◦ Perfect for occasional house sitting needs with one property</li>
                <li>◦ Free ID verification</li>
                <li>◦ Free Video calls with housesitters</li>
                <li>◦ Share up to 7 images on your profile</li>
                <li>◦ Automated printable housesit agreement emailed on acceptance of sitter</li>
                <li>◦ Verified reviews help you choose sitters with confidence.</li>
                <li>◦ Members can post one house sitting advert for one property at a time</li>
                <li>◦ Discounts available for upgrades</li>
                <li>◦ Free Livechat support</li>
              </ul>
            </li>
            <li>
              <div className='mt20'>
                <strong>CHOICE MATCH MEMBERSHIP £129 /year</strong>
              </div>
              <ul>
                <li>◦ Perfect account for home and pet owners needing flexibility</li>
                <li>◦ Unlimited housesitting for your main home and up to 2 other properties</li>
                <li>◦ Free ID Verification</li>
                <li>◦ Free Video calls with housesitters</li>
                <li>◦ Share up to 15 images per property profile</li>
                <li>◦ Automated printable housesit agreement emailed on acceptance of sitter</li>
                <li>◦ Verified reviews help you choose sitters with confidence</li>
                <li>◦ Free 24/7 Vet Professional Support</li>
                <li>◦ Discounts available for upgrades</li>
              </ul>
            </li>
            <li>
              <div className='mt20'>
                <strong>PREMIUM MATCH MEMBERSHIP £169/year</strong>
              </div>
              <ul>
                <li>◦ Designed for homeowners managing multiple properties</li>
                <li>◦ Unlimited housesitting for your main home and up to 2 other properties</li>
                <li>◦ Free ID verification</li>
                <li>◦ Free Video calls with housesitters</li>
                <li>◦ Share up to 20 photos per property profile</li>
                <li>◦ Automated printable housesit agreement emailed on acceptance of sitter</li>
                <li>◦ Verified reviews to help you choose sitter with confidence</li>
                <li>◦ Free 24/7 Vet Professional Support</li>
                <li>◦ Discounts available for upgrades to combined accounts</li>
                <li>◦ Free facility to boost your profile</li>
                <li>◦ Free account management support</li>
                <li>◦ Free Boost to your advert to gain maximum visibility</li>
              </ul>
            </li>
          </ul>

          <p className={css.info}>
            With flexible plans, you can pick the one that aligns with your budget and needs.
          </p>

          <p className={css.subtitle}>
            How to Join as a Homeowner
          </p>
          <p className={css.info}>
            Getting started with HouseSit Match is simple and quick! Follow these 3 easy steps to create your account, start connecting with trusted sitters and enjoy the benefits of house sitting:
          </p>
          <ul className={css.listNumbers}>
            <li>
              <strong>Register Your Account</strong> – Sign up as a homeowner or pet owner to create your profile and begin the process.
            </li>
            <li>
              <strong>Complete Your ID Verification</strong> – For your security, all members undergo an ID check. Don’t worry, we’re here to help make this process easy and seamless.
            </li>
            <li>
              <strong>Build Your Profile</strong> – Add detailed information about your home, pets, and any specific care needs.
            </li>
          </ul>

          <p className={css.info}>
            Once your profile is set up, you can post your house sitting advert and start browsing our extensive resources for pet owners. Whether you choose a Value, Choice or Premium membership, you’ll have access to everything you need to find the perfect sitter and ensure your home and pets are in safe hands.
          </p>

          <p className={css.subtitle}>
            FAQs from Homeowners & Pet Owners
          </p>

          <CollapseFAQ
            list={defaultFaq}
          />

          <p className={css.subtitle}>
            Start Enjoying Trusted Care Today
          </p>
          <p className={css.info}>
            Ready to experience the benefits of house sitting? Join HouseSit Match today to connect with reliable, verified sitters who’ll care for your home and pets like their own.
          </p>
          <p className={css.info}>
            Choose a membership plan that suits your lifestyle and start experiencing the peace of mind that comes with trusted care.
          </p>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default BenefitsHomeownersPage;
